import { useState, useEffect } from "react";

import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";

import { Visibility, VisibilityOff } from "@mui/icons-material";

import { AccountLayout } from "../../layouts";
import {
  getUserData,
  updateUserPassword,
  updateUserData,
} from "../../api/providers/userDataProviders";
import { LoadingScreen } from "../../ui/components/LoadingScreen";
import { UserInterface } from "../../interfaces";
import CustomModal from "../../components/public/CustomModal";

import accountImage from "../../assets/images/accountImage.jpeg";

interface ShowPasswordState {
  showOldPassword: boolean;
  showNewPassword: boolean;
  showRepeatNewPassword: boolean;
}

enum ShowPasswordFields {
  OldPassword = "showOldPassword",
  NewPassword = "showNewPassword",
  RepeatNewPassword = "showRepeatNewPassword",
}

export const UserDashboard = () => {
  const [user, setUser] = useState<UserInterface | null>(null);
  const [loading, setLoading] = useState(true);
  const [openEditProfile, setOpenEditProfile] = useState(false);
  const [openEditPassword, setOpenEditPassword] = useState(false);
  const [error, setError] = useState("");
  const [successModalTitle, setSuccessModal] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [userData, setUserData] = useState({
    name: user?.name || "",
    phoneNumber: user?.phoneNumber || "",
    identification: user?.identification || "",
  });
  const [passwords, setPasswords] = useState({
    oldPassword: "",
    newPassword: "",
    repeatNewPassword: "",
  });

  const [showPassword, setShowPassword] = useState<ShowPasswordState>({
    showOldPassword: false,
    showNewPassword: false,
    showRepeatNewPassword: false,
  });

  useEffect(() => {
    handleUserData();
  }, []);

  const handleUserData = async () => {
    try {
      setLoading(true);
      const result = await getUserData();
      if (result.ok) {
        setUser(result.data);
        setUserData({
          ...userData,
          phoneNumber: result.data.phoneNumber,
          identification: result.data.identification,
          name: result.data.name,
        });
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  const handleClickShowPassword = (field: ShowPasswordFields) => {
    setShowPassword((prevShowPassword) => ({
      ...prevShowPassword,
      [field]: !prevShowPassword[field],
    }));
  };

  const isButtonDisabled = () => {
    return (
      userData.name === user?.name &&
      userData.identification === user?.identification &&
      userData.phoneNumber === user?.phoneNumber
    );
  };

  const handleOpenEditProfile = () => setOpenEditProfile(true);
  const handleCloseEditProfile = () => setOpenEditProfile(false);

  const handleOpenEditPassword = () => setOpenEditPassword(true);
  const handleCloseEditPassword = () => setOpenEditPassword(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setPasswords((prevPasswords) => {
      const newPasswords = { ...prevPasswords, [id]: value };

      if (id === "newPassword" || id === "repeatNewPassword") {
        validatePasswords(newPasswords);
      }

      return newPasswords;
    });
  };

  const validatePasswords = (newPasswords: {
    oldPassword: string;
    newPassword: string;
    repeatNewPassword: string;
  }) => {
    const { newPassword, repeatNewPassword } = newPasswords;

    if (newPassword !== repeatNewPassword) {
      setError("Las contraseñas no coinciden.");
      setIsSaveDisabled(true);
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;
    if (!passwordRegex.test(newPassword)) {
      setError(
        "La contraseña debe contener al menos una mayúscula, una minúscula y un número."
      );
      setIsSaveDisabled(true);
      return;
    }

    setError("");
    setIsSaveDisabled(false);
  };

  type UpdatedFields = {
    name?: string;
    identification?: string;
    phoneNumber?: string;
  };

  const handleUpdateUserData = async () => {
    try {
      const updatedFields: UpdatedFields = {};

      if (userData.name !== user?.name) {
        updatedFields.name = userData.name;
      }
      if (userData.identification !== user?.identification) {
        updatedFields.identification = userData.identification;
      }
      if (userData.phoneNumber !== user?.phoneNumber) {
        updatedFields.phoneNumber = userData.phoneNumber;
      }

      if (Object.keys(updatedFields).length === 0) {
        console.log("No hay cambios para actualizar");
        return;
      }

      const result = await updateUserData(updatedFields);
      if (result.ok) {
        setUser(result.data);
        setSuccessModal("Información actualizada con éxito");
        setShowSuccessModal(true);
        handleCloseEditProfile();
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    }
  };

  const handleSaveNewPassword = async () => {
    if (!isSaveDisabled) {
      try {
        const result = await updateUserPassword({
          newPassword: passwords.newPassword,
          oldPassword: passwords.oldPassword,
        });
        if (result.ok) {
          handleCloseEditPassword();
          setSuccessModal("Contraseña cambiada con éxito");
          setShowSuccessModal(true);
        }
      } catch (error) {
        console.error("Error al actualizar la contraseña:", error);
      }
    }
  };

  const handleChangeUserData = (e) => {
    const { name, value } = e.target;
    setUserData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
  };

  return (
    <AccountLayout>
      <Container>
        <Grid container spacing={6} mt={"3%"} mb={"3%"} justifyContent={"center"}>

          <Grid item xs={12} mb={2}>
            <Typography
              variant="h4"
              mb={1}
              align="left"
              sx={{ color: "text.primary", fontWeight: "normal" }}
            >
              Mi cuenta
            </Typography>
          </Grid>

          <Grid item xs={12} sm={5} justifyContent="center">
            <Paper
              elevation={6}
              sx={{
                display: "flex",
                borderRadius: 2,
                height: "100%",
                flexDirection: "column",
                justifyContent: "center",
                backgroundColor: "#171023",
              }}
            >
              <Grid container>
                <Grid item xs={12} sx={{ p: 3 }}>
                  <TextField
                    id="outlined-name"
                    label="Nombre"
                    variant="outlined"
                    value={user && user.name}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      readOnly: true,
                    }}
                  />

                  <TextField
                    id="outlined-email"
                    label="Correo electrónico"
                    variant="outlined"
                    value={user && user.email}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      readOnly: true,
                    }}
                  />

                  <TextField
                    id="outlined-phone"
                    label="Telefono"
                    variant="outlined"
                    value={user && user.phoneNumber}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                  <TextField
                    id="outlined-identification"
                    label="Identificacion"
                    variant="outlined"
                    value={user && user.identification}
                    fullWidth
                    margin="normal"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12} mb={2} sx={{ p: 3 }}>
                  <Button
                    onClick={handleOpenEditProfile}
                    variant="contained"
                    color="primary"
                    sx={{ marginRight: 2, marginBottom: 2 }}
                  >
                    Editar Perfil
                  </Button>
                  <Button
                    onClick={handleOpenEditPassword}
                    variant="contained"
                    sx={{
                      marginBottom: 2,
                    }}
                  >
                    Editar Contraseña
                  </Button>

                  <Dialog
                    open={openEditProfile}
                    onClose={handleCloseEditProfile}
                  >
                    <DialogTitle>Editar Perfil</DialogTitle>
                    <DialogContent>
                      <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Nombre Completo"
                        type="text"
                        fullWidth
                        variant="standard"
                        name="name"
                        value={userData.name}
                        onChange={handleChangeUserData}
                      />
                      <TextField
                        autoFocus
                        margin="dense"
                        id="telephone"
                        label="Telefono"
                        type="text"
                        fullWidth
                        variant="standard"
                        name="phoneNumber"
                        value={userData.phoneNumber}
                        onChange={handleChangeUserData}
                      />
                      <TextField
                        autoFocus
                        margin="dense"
                        id="identification"
                        label="Identificacion ( Cedula )"
                        type="text"
                        fullWidth
                        variant="standard"
                        name="identification"
                        value={userData.identification}
                        onChange={handleChangeUserData}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleCloseEditProfile}>Cancelar</Button>
                      <Button
                        onClick={handleUpdateUserData}
                        disabled={isButtonDisabled()}
                      >
                        Guardar
                      </Button>
                    </DialogActions>
                  </Dialog>

                  <Dialog
                    open={openEditPassword}
                    onClose={handleCloseEditPassword}
                  >
                    <DialogTitle>Cambiar Contraseña</DialogTitle>
                    <DialogContent>
                      <TextField
                        autoFocus
                        margin="dense"
                        id="oldPassword"
                        label="Antigua Contraseña"
                        type={
                          showPassword.showOldPassword ? "text" : "password"
                        }
                        fullWidth
                        variant="standard"
                        value={passwords.oldPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() =>
                                  handleClickShowPassword(
                                    ShowPasswordFields.OldPassword
                                  )
                                }
                              >
                                {showPassword.showOldPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        onChange={handleInputChange}
                      />
                      <TextField
                        margin="dense"
                        id="newPassword"
                        label="Nueva Contraseña"
                        type={
                          showPassword.showNewPassword ? "text" : "password"
                        }
                        fullWidth
                        variant="standard"
                        value={passwords.newPassword}
                        onChange={handleInputChange}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() =>
                                  handleClickShowPassword(
                                    ShowPasswordFields.NewPassword
                                  )
                                }
                              >
                                {showPassword.showNewPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      <TextField
                        margin="dense"
                        id="repeatNewPassword"
                        label="Repetir Contraseña"
                        type={
                          showPassword.showRepeatNewPassword
                            ? "text"
                            : "password"
                        }
                        fullWidth
                        variant="standard"
                        value={passwords.repeatNewPassword}
                        onChange={handleInputChange}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() =>
                                  handleClickShowPassword(
                                    ShowPasswordFields.RepeatNewPassword
                                  )
                                }
                              >
                                {showPassword.showRepeatNewPassword ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />

                      {error && (
                        <Typography color="error" variant="body2">
                          {error}
                        </Typography>
                      )}
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleCloseEditPassword}>
                        Cancelar
                      </Button>
                      <Button
                        disabled={isSaveDisabled}
                        onClick={handleSaveNewPassword}
                      >
                        Guardar
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={5}>
            <Paper
              elevation={6}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: "darkBackground.main",
              }}
            >
              <Box
                component="img"
                src={accountImage}
                alt="Account"
                sx={{
                  width: "100%",
                  borderRadius: 1,
                  mb: 3,
                }}
              />
              <Box sx={{ mb: 7 }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    color: "text.primary",
                    fontWeight: "bold",
                    mb: 2,
                  }}
                >
                  ¿Te gustaría publicar tus eventos?
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary" }}>
                  Conviértete en organizador de eventos. Envíanos tus datos,
                  revisamos y aprobamos, y actualizamos tu cuenta para que
                  puedas empezar a gestionar eventos fácilmente y de manera
                  efectiva.
                </Typography>
              </Box>
              <Button variant="contained" color="primary">
                Comenzar
              </Button>
            </Paper>
          </Grid>

        </Grid>
        <CustomModal
          title={successModalTitle}
          onPress={handleCloseModal}
          isVisible={showSuccessModal}
        />
      </Container>
    </AccountLayout>
  );
};

export default UserDashboard;
